import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import getStudents, { Student } from '../libs/student-service'
import StudentTable from '../components/StudentTable'
import Element from '../components/Element'
import { Class } from '../libs/login-service'
import ClassSelector from '../components/ClassSelector'
import { getReport, ReportItem } from '../libs/report-service'
import { Batch, getBatches } from '../libs/batch-service'
import { ClassContext } from '../App'
import ReportBarChart from '../components/report/ReportBarChart'
import ScoringModal from '../components/report/ScoringModal'
import OutcomeModal from '../components/report/OutcomeModal'
import ReportDoughnutChart from '../components/report/ReportDoughnutChart'
import SetUpClasses from '../components/assessment/SetUpClasses'
import ReportSetUpClasses from '../components/report/ReportSetUpClasses'

const Wrapper = styled.div`
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    width: 100%;
    background-color: #ffffff;
    border-radius: 8px;
`

const ElementWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 90%;
`
const BlueBackground1 = styled.div`
    height: 80px;
    background-color: #e7effe;
    display: flex;
    padding: 20px;
    justify-content: center;
    align-items: center;
    gap: 10px;
`

const BlueBackground2 = styled.div`
    height: 80px;
    background-color: #e7effe;
    display: flex;
    padding: 20px;
    justify-content: center;
    align-items: center;
    gap: 50px;
`

const HeroWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 90%;
    gap: 20px;
`

const ElementContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
`
const Content = styled.label`
    text-align: left;
`

const Badge = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`

const Img = styled.img`
    height: 40px;
`

const AchievingLabel = styled.p`
    color: #00b88c;
`
const ProgressingLabel = styled.p`
    color: #f4bd50;
`

const DevelopingLabel = styled.p`
    color: #ed6a5e;
`

const HoveringText = styled.p`
    position: absolute;
    margin-top: -50px;
    color: white;
    background-color: grey;
    padding: 10px;
    border-radius: 8px;
`
const Header = styled.div`
    width: 100%;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #e5e6eb;
`

const HeadingButton = styled.button`
    padding: 5px 8px;
    background-color: #ffffff;
    border: 1px solid #c4c7cc;
    &:hover {
        cursor: pointer;
    }
`
interface Prop {
    setTitle: React.Dispatch<React.SetStateAction<string>>
}
export default function Report({ setTitle }: Prop) {
    useEffect(() => {
        setTitle('Report')
    }, [])
    const classes = useContext(ClassContext)
    const [students, setStudents] = useState<Student[]>([])
    const [report, setReport] = useState<ReportItem[]>([])
    const [selectedClass, setSelectedClass] = useState<Class>(classes[0])
    const [batch, setBatch] = useState<Batch | undefined>(
        selectedClass?.batches[0]
    )
    const [achievingHidden, setAchievingHidden] = useState(true)
    const [progressingHidden, setProgressingHidden] = useState(true)
    const [developingHidden, setDevelopingHidden] = useState(true)

    const [showScoring, setShowScoring] = useState(false)
    const [showOutcome, setShowOutcome] = useState(false)

    useEffect(() => {
        const fetchStudents = async () => {
            if (!selectedClass || !batch) {
                return
            }

            const { students } = await getStudents(selectedClass.id, batch.id)
            setStudents(students)
        }

        const fetchReport = async () => {
            if (!selectedClass || !batch) {
                return
            }

            const report = await getReport(selectedClass.id, batch.id)
            setReport(report)
        }

        fetchStudents()
        fetchReport()
    }, [selectedClass, batch])

    function handleClassUpdate(c: Class) {
        setSelectedClass(c)
        if (c.batches.length !== 0) {
            setBatch(c.batches[0])
        }
    }

    function handleShowScoringClick() {
        setShowScoring(!showScoring)
    }
    function handleShowOutcomeClick() {
        setShowOutcome(!showOutcome)
    }

    if (classes.length === 0 || students.length === 0) {
        return <ReportSetUpClasses setTitle={setTitle} />
    }

    return (
        <>
            {showScoring && (
                <ScoringModal handleCancel={handleShowScoringClick} />
            )}
            {showOutcome && (
                <OutcomeModal handleCancel={handleShowOutcomeClick} />
            )}
            <Wrapper>
                <Header>
                    Class Analysis Overview | Learn more about Moving The Next
                    Gen
                    <div>
                        <HeadingButton onClick={handleShowScoringClick}>
                            Scoring
                        </HeadingButton>
                        <HeadingButton onClick={handleShowOutcomeClick}>
                            Outcome
                        </HeadingButton>
                    </div>
                </Header>

                {report.map((r, i) => {
                    if (i % 2 === 0) {
                        return (
                            <ElementWrapper key={i}>
                                {report && report[i] && (
                                    <ReportDoughnutChart
                                        reportItem={report[i]}
                                    />
                                )}
                                {report && report[i + 1] && (
                                    <ReportDoughnutChart
                                        reportItem={report[i + 1]}
                                    />
                                )}
                            </ElementWrapper>
                        )
                    }
                })}
            </Wrapper>
            <Wrapper>
                <Header>
                    Individual Grades | Track students’ performance in details
                    here
                </Header>
                <ClassSelector
                    classes={classes}
                    selectedClass={selectedClass}
                    setSelectedClass={handleClassUpdate}
                    setBatch={setBatch}
                />
                <StudentTable
                    selectedClass={selectedClass}
                    batch={batch}
                    setStudent={() => {}}
                    badgeClick={() => {}}
                />
            </Wrapper>
        </>
    )
}
